import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";

import { Preloader } from "@/components/Preloader";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { CustomCursor } from "@/components/CustomCursor";
import { GooeyFilter } from "@/components/ui/liquid-toggle";
import { ThemeProvider } from "@/lib/theme";
import { LanguageProvider } from "@/lib/i18n";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Markets from "@/pages/Markets";
import Partners from "@/pages/Partners";
import Hubs from "@/pages/Hubs";
import Contact from "@/pages/Contact";

const SESSION_FLAG = "nexxis_preloader_seen";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);
  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/markets" element={<Markets />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/hubs" element={<Hubs />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  const [showPreloader, setShowPreloader] = useState(() => {
    try {
      return !sessionStorage.getItem(SESSION_FLAG);
    } catch {
      return true;
    }
  });

  // Cursor-following spotlight: updates --mx / --my on the hovered .nxx-card and .nxx-card-tilt
  // Also drives 3D tilt rotation for .nxx-card-tilt elements.
  useEffect(() => {
    // rAF-throttled so we do the work at most once per frame, no matter how
    // many mousemove events fire (keeps it smooth and cheap).
    let pending = null;
    let raf = null;
    const process = () => {
      raf = null;
      const e = pending;
      pending = null;
      if (!e || !e.target || !e.target.closest) return;
      const card = e.target.closest(".nxx-card, .nxx-card-tilt");
      if (!card) return;
      const r = card.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      card.style.setProperty("--mx", `${x}px`);
      card.style.setProperty("--my", `${y}px`);
      if (card.classList.contains("nxx-card-tilt")) {
        const px = x / r.width;
        const py = y / r.height;
        card.style.setProperty("--rx", `${((0.5 - py) * 9).toFixed(2)}deg`);
        card.style.setProperty("--ry", `${((px - 0.5) * 9).toFixed(2)}deg`);
      }
    };
    const onMove = (e) => {
      pending = e;
      if (!raf) raf = requestAnimationFrame(process);
    };
    const onOut = (e) => {
      const card = e.target && e.target.closest && e.target.closest(".nxx-card-tilt");
      if (!card) return;
      // Only reset when leaving the card (not entering a child)
      if (e.relatedTarget && card.contains(e.relatedTarget)) return;
      card.style.setProperty("--rx", "0deg");
      card.style.setProperty("--ry", "0deg");
    };
    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseout", onOut, true);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseout", onOut, true);
    };
  }, []);

  const handleDone = () => {
    try {
      sessionStorage.setItem(SESSION_FLAG, "1");
    } catch {}
    setShowPreloader(false);
  };

  return (
    <ThemeProvider>
      <LanguageProvider>
      <div className="App">
        <GooeyFilter />
        <BrowserRouter>
          {showPreloader && <Preloader onDone={handleDone} />}
          {!showPreloader && <CustomCursor />}
          <Nav />
          <ScrollToTop />
          <main>
            <AnimatedRoutes />
          </main>
          <Footer />
          <Toaster position="bottom-center" />
        </BrowserRouter>
      </div>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
